/* eslint-disable */
//@ts-nocheck
import React, { useEffect, useState, useContext, useCallback } from "react";
import { Layout, Space, Typography, Dropdown, Button, Avatar, Divider, Drawer, Flex } from "antd";
import {
  MessageOutlined,
  QuestionCircleOutlined,
  UserOutlined,
  MenuOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TrophyOutlined,
  ReadOutlined,
} from "@ant-design/icons";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { theme } from "antd";
import QLogo from "./Logo";
import QIcon from "components/layout/QIcon";
import QLayoutContext from "contexts/Layout";
import { useComments, useHelp, useTasks, useUserSetting } from "hooks/layout";
import ReactGA from "react-ga4";
import GAMap from "tokens/GAMap.json";
import TasksOutlined from "assets/icons/Tasks-Outlined.svg";
import AppSearch from "./AppSearch";

const { useToken } = theme;

const { Header } = Layout;
const { Text, Link, Paragraph } = Typography;

const trackGAEvent = (action) => {
  ReactGA.event({
    category: GAMap.header.CATEGORY,
    action: action,
  });
};

// Opens Userpilot's resource centre, which the tenant layout wires up and exposes as
// `window.qoyodResourceCenter`. That happens asynchronously, so until the controller is
// published there is nothing to open and the button stays out of the header rather than
// sitting there doing nothing -- which is also how it degrades when Userpilot is blocked.
export const ResourceCenterButton = ({ compact = false }) => {
  const { t } = useTranslation();
  const [ready, setReady] = useState(() => Boolean(window.qoyodResourceCenter?.isReady()));

  useEffect(() => {
    if (ready) return;

    const onReady = () => setReady(true);

    document.addEventListener("qoyod:resource-center-ready", onReady);
    return () => document.removeEventListener("qoyod:resource-center-ready", onReady);
  }, [ready]);

  if (!ready) return null;

  const label = t("frontend.tooltips.on_boarding.resource_center");
  const openResourceCenter = () => {
    trackGAEvent(GAMap.header.help.resourceCenter);
    window.qoyodResourceCenter?.toggle();
  };

  return compact ? (
    <Button
      id="resourceCenter"
      type="text"
      aria-label={label}
      icon={<ReadOutlined />}
      onClick={openResourceCenter}
    />
  ) : (
    <Button id="resourceCenter" icon={<ReadOutlined />} onClick={openResourceCenter}>
      {label}
    </Button>
  );
};

const mobileHeader = new Map<string, unknown>([
  ["Position", "absolute"],
  [
    "Content",
    ({
      triggerCollapse,
      comments,
      tasks,
      helpItems,
      userSettings,
      getLatestTasks,
      showReferral,
      hideSidebar,
    }) => {
      const [open, setOpen] = useState(false);
      const [items, setItems] = useState([]);

      const showDrawer = () => {
        setOpen(true);
      };

      const onClose = () => {
        setOpen(false);
      };

      useEffect(() => {
        setItems(tasks);
      }, [tasks]);

      return (
        <>
          <Space>
            {showReferral && (
              <Button
                icon={<TrophyOutlined style={{ color: "#1890ff" }} />}
                onClick={() => {
                  window.location.href = "/tenant/referrals";
                }}
              />
            )}
            <ResourceCenterButton compact />
            <Button
              onClick={() => {
                setItems(comments);
                showDrawer();
                trackGAEvent(GAMap.header.mentions.expand);
              }}
              type="text"
              icon={<MessageOutlined />}
            />
            <AppSearch compact />
            <Button
              onClick={() => {
                setItems(helpItems);
                showDrawer();
              }}
              type="text"
              icon={<QuestionCircleOutlined />}
            />
            <Button
              onClick={() => {
                getLatestTasks();
                showDrawer();
                trackGAEvent(GAMap.header.tasks.expand);
              }}
              type="text"
              icon={<QIcon component={TasksOutlined} />}
            />
            <Button
              onClick={() => {
                setItems(userSettings);
                showDrawer();
              }}
              type="text"
              icon={<UserOutlined />}
            />
            {!hideSidebar && (
              <Button type="text" icon={<MenuOutlined />} onClick={triggerCollapse} />
            )}
          </Space>
          <Drawer placement="top" onClose={onClose} open={open} closeIcon={null}>
            <Space direction="vertical" style={{ width: "100%" }}>
              {items.map((item) => item.label)}
            </Space>
          </Drawer>
        </>
      );
    },
  ],
]);

const pcHeader = new Map<string, unknown>([
  ["Position", "relative"],
  [
    "Content",
    ({
      triggerCollapse,
      collapsed,
      currentUser,
      organizationName,
      tenantID,
      comments,
      tasks,
      helpItems,
      userSettings,
      getLatestTasks,
      showReferral,
      hideSidebar,
    }) => (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flex: "1 1 220px",
            minWidth: 0,
          }}
        >
          {!hideSidebar && (
            <Button
              type="text"
              icon={
                collapsed ? (
                  <MenuUnfoldOutlined
                    style={{ transform: i18next.dir() === "rtl" ? "rotate(180deg)" : "" }}
                  />
                ) : (
                  <MenuFoldOutlined
                    style={{ transform: i18next.dir() === "rtl" ? "rotate(180deg)" : "" }}
                  />
                )
              }
              onClick={triggerCollapse}
              style={{
                padding: 0,
              }}
            />
          )}
          <Paragraph
            style={{ margin: 0, width: hideSidebar ? "100%" : "calc(100% - 40px)" }}
            ellipsis={{ rows: 1 }}
          >
            {useTranslation().t("activerecord.attributes.layout.welcome")}{" "}
            <Link
              href={currentUser.href}
              onClick={() => trackGAEvent(GAMap.header.editProfile)}
              strong
            >
              {currentUser.label}
            </Link>{" "}
            {useTranslation().t("activerecord.attributes.layout.in_orgnization")}{" "}
            <Text strong>{organizationName}</Text>
          </Paragraph>
        </div>
        <AppSearch />
        <Space size={16} split={<Divider type="vertical" />}>
          <Space>
            {showReferral && (
              <Button
                icon={<TrophyOutlined style={{ color: "#1890ff" }} />}
                onClick={() => {
                  window.location.href = "/tenant/referrals";
                }}
              >
                {useTranslation().t("referrals.name")}
              </Button>
            )}
            <ResourceCenterButton />
            <Dropdown
              onOpenChange={(opened) =>
                trackGAEvent(opened ? GAMap.header.mentions.expand : GAMap.header.mentions.collapse)
              }
              menu={{
                items: comments,
                style: {
                  width: 443,
                  maxHeight: "90vh",
                  overflow: "auto",
                  border: "1px solid rgba(0, 0, 0, 10%)",
                  padding: 0,
                },
              }}
              trigger={["click"]}
            >
              <Button icon={<MessageOutlined />} />
            </Dropdown>
            <Dropdown
              menu={{ items: helpItems, style: { border: "1px solid rgba(0, 0, 0, 10%)" } }}
              trigger={["click"]}
            >
              <Button icon={<QuestionCircleOutlined />} />
            </Dropdown>
            <Dropdown
              onOpenChange={(opened) => {
                trackGAEvent(opened ? GAMap.header.tasks.expand : GAMap.header.tasks.collapse);
                opened && getLatestTasks();
              }}
              menu={{
                items: tasks,
                style: {
                  width: 443,
                  maxHeight: "90vh",
                  overflow: "auto",
                  border: "1px solid rgba(0, 0, 0, 10%)",
                  padding: 0,
                },
              }}
              trigger={["click"]}
            >
              <Button icon={<QIcon style={{ fill: "black" }} component={TasksOutlined} />} />
            </Dropdown>
          </Space>

          <Space>
            {tenantID && (
              <>
                <Avatar shape="square" size="large" src={currentUser.tenantLogo} />
                <Flex vertical style={{ lineHeight: "1.2" }}>
                  {organizationName && (
                    <Text
                      strong
                      style={{ maxWidth: "18ch" }}
                      ellipsis={{ tooltip: organizationName }}
                    >
                      {organizationName}
                    </Text>
                  )}
                  <Text style={{ color: "#13778D" }}># {tenantID}</Text>
                </Flex>
                <Divider type="vertical" />
              </>
            )}
            <Dropdown
              menu={{
                items: userSettings,
                style: { border: "1px solid rgba(0, 0, 0, 0.1)" },
              }}
              onClick={() => {}}
              trigger={["click"]}
            >
              <Button icon={<MenuOutlined />} style={{ border: "none", boxShadow: "none" }} />
            </Dropdown>
          </Space>
        </Space>
      </div>
    ),
  ],
]);

const QHeader = ({
  broken,
  collapsed,
  logoURL,
  triggerCollapse,
  startIntro,
  hideSidebar = false,
}) => {
  const { token } = useToken();
  const [headerView, setHeaderView] = useState(null);
  const { currentUser, organizationName, showReferral, tenantID } = useContext(QLayoutContext);
  const comments = useComments();
  const { tasks, getLatestTasks } = useTasks();
  const helpItems = useHelp(broken, startIntro);
  const userSettings = useUserSetting();

  const menuTrigger = useCallback(() => {
    triggerCollapse();
    const eventID = collapsed ? GAMap.header.menuTrigger.expand : GAMap.header.menuTrigger.collapse;
    trackGAEvent(eventID);
  }, [triggerCollapse]);

  useEffect(() => {
    if (broken) {
      setHeaderView(mobileHeader);
    } else {
      setHeaderView(pcHeader);
    }
  }, [broken]);

  const HeaderContent = headerView?.get("Content") ?? (() => null);

  return (
    <Header
      style={{
        padding: 0,
        background: token.colorBgContainer,
        width: "100%",
        left: 0,
        position: headerView?.get("Position"),
      }}
    >
      <div
        style={{ paddingInline: token.paddingMD, display: "flex", justifyContent: "space-between" }}
      >
        {broken ? <QLogo logoURL={logoURL} logoHeight={"80%"} /> : null}
        <HeaderContent
          triggerCollapse={menuTrigger}
          collapsed={collapsed}
          currentUser={currentUser}
          organizationName={organizationName}
          tenantID={tenantID}
          comments={comments}
          tasks={tasks}
          helpItems={helpItems}
          userSettings={userSettings}
          getLatestTasks={getLatestTasks}
          showReferral={showReferral}
          hideSidebar={hideSidebar}
        />
      </div>
    </Header>
  );
};

export default QHeader;
